export function appContactTrackingPayload(dataset = {}) {
  return {
    event: 'lp_app_contact_click',
    lp_app: dataset.app || 'whatsapp',
    lp_store: dataset.store || 'laserpecker-us',
    lp_page_type: dataset.pageType || '',
    lp_position: dataset.position || '',
  }
}

export function pushAppContactClick(dataset, host = globalThis.window) {
  if (!host) return null
  const payload = appContactTrackingPayload(dataset)
  host.dataLayer = host.dataLayer || []
  if (typeof host.dataLayer.push !== 'function') return null
  host.dataLayer.push(payload)
  return payload
}

const PRODUCT_FIXED_BAR_SELECTOR = '[data-main-product-figma] [data-role="fixed-bar"]'
const PRODUCT_BOTTOM_PROPERTY = '--app-contact-product-bottom-offset'
const PRODUCT_FIXED_BAR_GAP = 16
const MAX_TRANSITION_FRAMES = 24
const FIXED_BAR_TRANSITION_EVENTS = [
  'transitionrun',
  'transitionstart',
  'transitionend',
  'transitioncancel',
]

if (typeof window !== 'undefined' && typeof HTMLElement !== 'undefined') {
  class AppContactEntry extends HTMLElement {
    connectedCallback() {
      if (this.connected) return
      this.connected = true
      this.link = this.querySelector('[data-app-contact-link]')
      this.closeButton = this.querySelector('[data-app-contact-close]')
      this.onLinkClick = () => pushAppContactClick(this.dataset, window)
      this.onClose = () => {
        this.hidden = true
        this.stopProductFixedBarAvoidance()
        try {
          window.sessionStorage?.setItem(this.dataset.storageKey, '1')
        } catch {
          // The static WhatsApp link remains usable when storage is unavailable.
        }
      }

      this.link?.addEventListener('click', this.onLinkClick)
      this.closeButton?.addEventListener('click', this.onClose)

      if (this.closeButton && this.dataset.storageKey && !window.Shopify?.designMode) {
        try {
          this.hidden = window.sessionStorage?.getItem(this.dataset.storageKey) === '1'
        } catch {
          this.hidden = false
        }
      }

      if (!this.hidden && this.isProductFloatingEntry()) {
        this.startProductFixedBarAvoidance()
      }
    }

    disconnectedCallback() {
      this.link?.removeEventListener('click', this.onLinkClick)
      this.closeButton?.removeEventListener('click', this.onClose)
      this.stopProductFixedBarAvoidance()
      this.connected = false
    }

    isProductFloatingEntry() {
      return (
        this.classList?.contains('app-contact-entry--floating') &&
        this.dataset.pageType === 'product'
      )
    }

    startProductFixedBarAvoidance() {
      if (this.productAvoidanceActive) return

      this.productAvoidanceActive = true
      this.productAbortController = new AbortController()
      const { signal } = this.productAbortController
      this.onProductViewportChange = () => this.updateProductFixedBarPosition()
      this.onProductSectionLoad = () => this.bindProductFixedBar()
      this.onProductFixedBarTransition = (event) => {
        if (event.target !== this.productFixedBar || event.propertyName !== 'transform') return

        if (event.type === 'transitionrun' || event.type === 'transitionstart') {
          this.startProductTransitionTracking()
          return
        }

        this.stopProductTransitionTracking()
        this.updateProductFixedBarPosition()
      }

      window.addEventListener('resize', this.onProductViewportChange, { passive: true, signal })
      document.addEventListener('shopify:section:load', this.onProductSectionLoad, { signal })
      this.bindProductFixedBar()
    }

    bindProductFixedBar() {
      const nextFixedBar = document.querySelector(PRODUCT_FIXED_BAR_SELECTOR)

      if (nextFixedBar === this.productFixedBar) {
        this.updateProductFixedBarPosition()
        return
      }

      this.unbindProductFixedBar()
      this.productFixedBar = nextFixedBar

      if (nextFixedBar) {
        this.productFixedBarAbortController = new AbortController()
        const { signal } = this.productFixedBarAbortController

        for (const eventName of FIXED_BAR_TRANSITION_EVENTS) {
          nextFixedBar.addEventListener(eventName, this.onProductFixedBarTransition, { signal })
        }

        if (typeof window.MutationObserver === 'function') {
          this.productFixedBarObserver = new MutationObserver(() =>
            this.updateProductFixedBarPosition(),
          )
          this.productFixedBarObserver.observe(nextFixedBar, {
            attributes: true,
            attributeFilter: ['data-visible', 'style'],
          })
        }

        if (typeof window.ResizeObserver === 'function') {
          this.productFixedBarResizeObserver = new ResizeObserver(() =>
            this.updateProductFixedBarPosition(),
          )
          this.productFixedBarResizeObserver.observe(nextFixedBar)
        }
      }

      this.updateProductFixedBarPosition()
    }

    updateProductFixedBarPosition() {
      const fixedBar = this.productFixedBar
      const rectangle = fixedBar?.getBoundingClientRect?.()
      const viewportHeight = window.innerHeight || document.documentElement?.clientHeight || 0
      const visible =
        (window.innerWidth <= 699 || this.dataset.store === 'laserpeckerjp') &&
        fixedBar?.hasAttribute?.('data-visible') &&
        !fixedBar.hidden &&
        fixedBar.getAttribute?.('aria-hidden') !== 'true' &&
        rectangle?.height > 0 &&
        rectangle.bottom > 0 &&
        rectangle.top < viewportHeight

      if (!visible) {
        this.style.removeProperty(PRODUCT_BOTTOM_PROPERTY)
        return
      }

      const bottom = Math.ceil(Math.max(0, viewportHeight - rectangle.top) + PRODUCT_FIXED_BAR_GAP)
      this.style.setProperty(PRODUCT_BOTTOM_PROPERTY, `${bottom}px`)
    }

    startProductTransitionTracking() {
      if (this.productTransitionFrame !== undefined) return

      this.productTransitionFrameCount = 0
      this.updateProductFixedBarPosition()
      this.queueProductTransitionFrame()
    }

    queueProductTransitionFrame() {
      if (
        this.productTransitionFrameCount >= MAX_TRANSITION_FRAMES ||
        typeof window.requestAnimationFrame !== 'function'
      ) {
        this.productTransitionFrame = undefined
        return
      }

      this.productTransitionFrame = window.requestAnimationFrame(() => {
        this.productTransitionFrame = undefined
        if (!this.productFixedBar || this.hidden) return

        this.productTransitionFrameCount += 1
        this.updateProductFixedBarPosition()
        this.queueProductTransitionFrame()
      })
    }

    stopProductTransitionTracking() {
      if (
        this.productTransitionFrame !== undefined &&
        typeof window.cancelAnimationFrame === 'function'
      ) {
        window.cancelAnimationFrame(this.productTransitionFrame)
      }

      this.productTransitionFrame = undefined
      this.productTransitionFrameCount = 0
    }

    unbindProductFixedBar() {
      this.stopProductTransitionTracking()
      this.productFixedBarAbortController?.abort()
      this.productFixedBarObserver?.disconnect()
      this.productFixedBarResizeObserver?.disconnect()
      this.productFixedBarAbortController = undefined
      this.productFixedBarObserver = undefined
      this.productFixedBarResizeObserver = undefined
      this.productFixedBar = undefined
    }

    stopProductFixedBarAvoidance() {
      if (!this.productAvoidanceActive) return

      this.productAbortController?.abort()
      this.unbindProductFixedBar()
      this.style.removeProperty(PRODUCT_BOTTOM_PROPERTY)
      this.productAbortController = undefined
      this.productAvoidanceActive = false
    }
  }

  if (!customElements.get('app-contact-entry')) {
    customElements.define('app-contact-entry', AppContactEntry)
  }
}
